import type { CreativeId } from '@ad-lucy/config/creatives/types';
import { createContext, type ReactElement, useContext, useMemo } from 'react';
import {
  adSpecificCreativesTrackingConfig,
  creativesTrackingConfig,
} from '../../config/tracking/creativesTrackingConfig';
import type { CreativesTrackingConfig, TrackingGlobalMetaData } from '../../config/tracking/types';
import type { GlobalMeta } from '../adData/globalData/types';
import { useGlobalConfig } from '../adData/globalData/useGlobalConfig';

export const TrackingContext = createContext({} as CreativesTrackingConfig);

function parseGlobalMetaData(
  defaultGlobalMeta: TrackingGlobalMetaData,
  adSpecificGlobalMeta?: TrackingGlobalMetaData,
  globalConfig?: GlobalMeta,
): TrackingGlobalMetaData {
  const trackingGlobalMetaData: TrackingGlobalMetaData = {
    ...defaultGlobalMeta,
    ...(adSpecificGlobalMeta || {}),
  };

  Object.keys(trackingGlobalMetaData).forEach((key) => {
    const globalValue = globalConfig?.[key as keyof GlobalMeta];
    if (globalValue !== undefined) {
      trackingGlobalMetaData[key as keyof TrackingGlobalMetaData] =
        globalValue as TrackingGlobalMetaData[keyof TrackingGlobalMetaData];
    }
  });
  return trackingGlobalMetaData;
}

export const TrackingProvider = ({
  children,
  creativesId,
}: {
  children: ReactElement | ReactElement[];
  creativesId: CreativeId;
}): JSX.Element => {
  const globalConfig = useGlobalConfig();

  const trackingConfig = useMemo(() => {
    const {
      globalTrackingMetaDataDefaults: defaultGlobalMeta,
      builderComponents: defaultBuilderComponents,
      ...defaultConfig
    } = creativesTrackingConfig;
    const {
      globalTrackingMetaDataDefaults: adSpecificGlobalMeta,
      builderComponents: adSpecificBuilderComponents,
      ...adSpecificDefaultConfig
    } = adSpecificCreativesTrackingConfig[creativesId] || {};
    return {
      ...defaultConfig,
      ...adSpecificDefaultConfig,
      builderComponents: {
        ...defaultBuilderComponents,
        ...adSpecificBuilderComponents,
      },
      // Set global meta data
      globalTrackingMetaData: parseGlobalMetaData(
        defaultGlobalMeta,
        adSpecificGlobalMeta,
        globalConfig.data?.meta,
      ),
    };
  }, [creativesId, globalConfig]);

  return (
    <TrackingContext.Provider value={trackingConfig as CreativesTrackingConfig}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTrackingConfig = (): CreativesTrackingConfig => {
  const context = useContext(TrackingContext);
  return context;
};
